import '.d{display:flex;}.ssr-plugin'
import '.an{justify-content:center;}.ssr-plugin'
import '.bc{padding-block:8.75rem;}.ssr-plugin'
import '.bd{color:var(--palette-blue-300);}.ssr-plugin'
import { Title } from '@app/components/core/typography/Title'
import { PageLayout } from './layout/PageLayout'
import { Banner } from '@app/components/core/banner/Banner'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { styled } from 'inlines'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useLanguage, useTranslations } from '@i18n/useLanguage'
import { i18n } from '@i18n/index'
import { ReactNode, useEffect, useState } from 'react'
import { PageMediaGallery } from '@app/components/blocks/mediaGallery/PageMediaGallery'
import { Text } from '@app/components/core/typography/Text'
import { Tag } from '@app/components/core/tag/Tag'
import { UserInitiativeCard } from '@app/components/blocks/card/UserInitiativeCard'
import { useClient } from '@based/sdk/react'
import useMediaQuery from '@app/util/useMediaQuery'
import { fullName } from '@app/util/helpers'
import { ParagraphedText } from '@app/components/core/typography/ParagraphedText'
import { TagModel } from '@app/types/TagModel'
import { ProfileView } from 'functions/app/profile/view'

export type ProfilePageProps = {
  userId: string
}

export const ProfilePage = ({ userId }: ProfilePageProps) => {
  const isMobile = useMediaQuery()
  const [profile, setProfile] = useState<ProfileView>()
  const client = useClient()
  useEffect(() => {
    const fetchProfile = async () => {
      const result = await client.query('view-profile', { id: userId }).get()
      setProfile(result)
    }

    void fetchProfile()
  }, [userId, client])

  if (!profile) {
    return (
      <PageLayout gap="3rem">
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ marginBottom: '25rem' }}
        >
          <Banner>
            <Avatar
              size={isMobile ? 'medium' : 'large'}
              variant="yellow"
              labelSource=""
            />
          </Banner>
        </Column>
      </PageLayout>
    )
  }

  const name = fullName(profile)

  return (
    <PageLayout gap="3rem">
      <Column gap="1.5rem" alignItems="stretch">
        <Banner image={profile.banner?.src}>
          <Avatar
            size={isMobile ? 'medium' : 'large'}
            variant="yellow"
            labelSource={name}
            image={profile.avatar}
          />
        </Banner>
        <Row style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Column gap="1.5rem" style={{ flex: '1 1 400px' }}>
            {isMobile ? (
              <Title.H4>{name}</Title.H4>
            ) : (
              <Title.H2>{name}</Title.H2>
            )}
            {/* {profile.badge && (
              <Tag
                variant="tertiary"
                size="small"
                label={profile.badge}
                style={{ alignSelf: 'start' }}
              />
            )} */}
          </Column>

          {/* <Button
            icon={faUserPlus}
            iconPosition="left"
            size={isMobile ? 'small' : 'medium'}
            label={translations.connect}
            onClick={() => {}}
          /> */}
        </Row>
      </Column>
      <Row gap="4.75rem" style={{ alignItems: 'start', flexWrap: 'wrap' }}>
        <LeftColumn name={name} profile={profile} />
        <RightColumn profile={profile} />
      </Row>
    </PageLayout>
  )
}

const LeftColumn = ({
  name,
  profile: { bio, livingDream, media, firstName },
}: {
  name: string
  profile: {
    bio?: string
    livingDream?: string
    media: { src: string }[]
    firstName: string
  }
}) => {
  const [language, _] = useLanguage()
  const translations = i18n.languages[language].app.profilePage
  return (
    <Column alignItems="stretch" gap="3rem" style={{ flex: '2 1 400px' }}>
      <LeftColumnItem header={translations.bio}>
        {bio ? (
          <ParagraphedText>{bio}</ParagraphedText>
        ) : (
          <ContentPlaceholder
            text={`${firstName} ${translations.emptyProfileMessage}`}
          />
        )}
      </LeftColumnItem>
      <LeftColumnItem
        header={translations.livingDream}
        isVisible={!!livingDream}
      >
        <ParagraphedText>{livingDream}</ParagraphedText>
      </LeftColumnItem>
      <LeftColumnItem header={translations.media} isVisible={media.length > 0}>
        <PageMediaGallery
          title={name}
          images={media.map((image) => image.src)}
        />
      </LeftColumnItem>
    </Column>
  )
}

const RightColumn = ({
  profile,
}: {
  profile: Pick<
    ProfileView,
    | 'location'
    | 'livingEnvironment'
    | 'searchRegions'
    | 'housingForms'
    | 'interests'
    | 'initiatives'
  >
}) => {
  const t = useTranslations().app
  const {
    location,
    livingEnvironment,
    searchRegions,
    housingForms,
    interests,
    initiatives,
  } = profile
  return (
    <Column
      gap="0rem"
      alignItems="stretch"
      style={{ flex: '1 1 200px', alignSelf: 'start' }}
    >
      <RightColumnItem header={t.profilePage.placeOfResidence}>
        <Text.Medium style={{ color: 'var(--palette-blue-600)' }}>
          {location?.name || t.common.unknownLocation}
        </Text.Medium>
      </RightColumnItem>
      <RightColumnItem
        header={t.profilePage.initiatives}
        isVisible={initiatives.length > 0}
      >
        <Column gap="0.5rem">
          {initiatives.map((initiative) => (
            <UserInitiativeCard
              id={initiative.id}
              key={initiative.id}
              slug={initiative.slug}
              title={initiative.title}
              image={initiative.images?.[0]?.src}
              subTitle={initiative.description}
            />
          ))}
        </Column>
      </RightColumnItem>
      <TagsColumnItem
        header={t.profilePage.followedHousingForms}
        isVisible={housingForms.length > 0}
        tags={housingForms.map((housingForm) => ({
          key: housingForm.id,
          value: housingForm.title,
        }))}
      />
      <RightColumnItem header={t.profilePage.searchRegion}>
        <Text.Medium style={{ color: 'var(--palette-blue-600)' }}>
          {searchRegions.map((region) => region.name).join(', ')}
        </Text.Medium>
      </RightColumnItem>
      <RightColumnItem header={t.profilePage.livingEnvironment}>
        <Text.Medium style={{ color: 'var(--palette-blue-600)' }}>
          {livingEnvironment?.name}
        </Text.Medium>
      </RightColumnItem>
      <TagsColumnItem
        header={t.profilePage.interests}
        isVisible={interests.length > 0}
        tags={interests.map((interest) => ({
          key: interest.id,
          value: interest.name,
        }))}
      />
    </Column>
  )
}

const LeftColumnItem = ({
  header,
  isVisible = true,
  children,
}: {
  header: string
  isVisible?: boolean
  children: ReactNode
}) => {
  if (!isVisible) {
    return null
  }
  return (
    <Column alignItems="stretch">
      <Title.H5>{header}</Title.H5>
      {children}
    </Column>
  )
}

const RightColumnItem = ({
  header,
  isVisible = true,
  children,
}: {
  header: string
  isVisible?: boolean
  children: ReactNode
}) => {
  if (!isVisible) {
    return null
  }
  return (
    <Column
      alignItems="stretch"
      style={{
        paddingBlock: '1rem',
        borderBottom: '1px solid var(--gray-300)',
      }}
    >
      <Title.H5>{header}</Title.H5>
      {children}
    </Column>
  )
}

const TagsColumnItem = ({
  header,
  isVisible = true,
  tags,
}: {
  header: string
  isVisible?: boolean
  tags: TagModel[]
}) => {
  return (
    <RightColumnItem header={header} isVisible={isVisible}>
      <Row gap="0.5rem" style={{ flexWrap: 'wrap', rowGap: '1rem' }}>
        {tags.map((tag) => (
          <Tag key={tag.key} variant={'transparent'} label={tag.value} />
        ))}
      </Row>
    </RightColumnItem>
  )
}

const ContentPlaceholder = ({ text }: { text: string }) => {
  return (
    <div className="d an bc bd"
    >
      {text}
    </div>
  )
}
